import { useState, useCallback, useEffect } from "react";
import {
  FileText, Image as ImageIcon, Trash2, Pencil, MessageCircle, History,
  AlertCircle, Send, Paperclip, Ban, RotateCcw, CalendarClock, CheckCircle2,
} from "lucide-react";
import Modal from "../components/Modal";
import Badge from "../components/Badge";
import VinculoManutencao from "../components/VinculoManutencao";
import {
  SISTEMAS, MECH_SPECIALTIES, TIPOS_ESTABELECIMENTO, TIPOS_VEICULO,
  ASSISTENCIA_TIPOS, servicoLabel, resumoAtendimento, resumoPerfil, docsEntreguesNoSistema,
  secoesDaFicha,
} from "../constants";
import { getSignedUrl } from "../lib/storage";
import { motivoDaRecusa } from "../lib/rede";
import { credStatus, waLink, getToday, addMonths, fmtDataHora } from "../utils";
import { encodeFichaToken, correcaoWhatsappUrl, documentosWhatsappUrl } from "../lib/fichaLink";
import { emitirCartaoCNPJ } from "../lib/cnpjComprovante";

// ═══════════════════════════════════════════════════════════════
// DETALHE DA FICHA — o mesmo em qualquer lugar do CRM
// ═══════════════════════════════════════════════════════════════
//
// Isto morava dentro da página "Fichas / Credenciamento", e era o que ainda
// obrigava a equipe a trocar de aba: da Rede dava para ver que faltava
// documento, mas pedir o documento, emitir o Cartão CNPJ ou olhar o vínculo
// com os sistemas só na outra tela. Agora é um componente só, aberto dos dois
// lugares, com a mesma regra e o mesmo histórico.

const ACAO_LABEL = {
  criacao: "Ficha criada", edicao: "Dados atualizados", assinatura: "Assinatura coletada",
  anexo: "Documento anexado", remocao: "Documento removido", conclusao: "Credenciamento concluído",
  sem_interesse: "Sem interesse registrado", retomada: "Contato retomado",
};
const ACAO_COLOR = {
  criacao: "#6366f1", edicao: "#3b82f6", assinatura: "#0d9488", anexo: "#7c3aed",
  remocao: "#ef4444", conclusao: "#059669", sem_interesse: "#d97706", retomada: "#7c3aed",
};


export default function FichaDetalhe({
  ficha, onClose, setCredenciamentos, onEdit, onExcluir, userName = "",
}) {
  const [docsNosSistemas, setDocsNosSistemas] = useState(new Set());
  const [emitindo, setEmitindo] = useState(false);
  const [emitirMsg, setEmitirMsg] = useState("");
  const [noInterestOpen, setNoInterestOpen] = useState(false);
  const [resumeDate, setResumeDate] = useState("");
  const [semIntMotivo, setSemIntMotivo] = useState("");
  const [erroMotivo, setErroMotivo] = useState("");
  const [verHistorico, setVerHistorico] = useState(false);
  // Edit rápido: trocar nome e contato sem abrir a ficha inteira. Serve
  // principalmente para o registro rápido, que nasce só com nome e telefone.
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});

  // O que os sistemas já receberam deste fornecedor, por consulta ao vivo.
  // Documento enviado direto lá não aparece na lista do CRM — sem isto, a
  // ficha cobraria de novo o que ele já entregou.
  const registrarEstadoSistema = useCallback((_sistema, dados) => {
    const entregues = docsEntreguesNoSistema(dados);
    if (!entregues.length) return;
    setDocsNosSistemas((prev) => {
      const proximo = new Set(prev);
      let mudou = false;
      for (const t of entregues) if (!proximo.has(t)) { proximo.add(t); mudou = true; }
      return mudou ? proximo : prev;
    });
  }, []);

  // Cada ficha começa sem nada sabido: o que valia para um fornecedor não vale
  // para o próximo.
  const fichaId = ficha?.id;
  useEffect(() => {
    setDocsNosSistemas(new Set());
    setEmitirMsg(""); setNoInterestOpen(false); setVerHistorico(false); setEditando(false);
  }, [fichaId]);

  if (!ficha) return null;
  const c = ficha;

  const abrirEdicao = () => {
    setForm({
      nomeFantasia: c.nomeFantasia || "", razaoSocial: c.razaoSocial || "",
      responsavelNome: c.responsavelNome || "",
      telefone: c.telefone || "", celular: c.celular || "", email: c.email || "",
    });
    setEditando(true);
  };
  const salvarEdicao = () => {
    setCredenciamentos((prev) => prev.map((x) => (x.id !== c.id ? x : {
      ...x,
      nomeFantasia: form.nomeFantasia?.trim() || null,
      razaoSocial: form.razaoSocial?.trim() || null,
      responsavelNome: form.responsavelNome?.trim() || null,
      telefone: form.telefone?.trim() || null,
      celular: form.celular?.trim() || null,
      email: form.email?.trim() || null,
      historico: [...(Array.isArray(x.historico) ? x.historico : []), {
        ts: new Date().toISOString(), acao: "edicao", autor: userName,
        detalhe: "Nome e contatos editados na ficha.",
      }],
    })));
    setEditando(false);
  };
  const st = credStatus(c);
  const wa = waLink(c.celular || c.telefone);
  const esp = (c.especialidades || []).map((id) => MECH_SPECIALTIES.find((s) => s.id === id)?.label).filter(Boolean);
  const serv = (c.servicos || []).map(servicoLabel).filter(Boolean);
  const veic = (c.tiposVeiculo || []).map((id) => TIPOS_VEICULO.find((t) => t.id === id)?.label).filter(Boolean);
  const assist = (c.tiposAssistencia || []).map((id) => ASSISTENCIA_TIPOS.find((a) => a.id === id)?.label).filter(Boolean);
  const ramo = resumoPerfil(c.tipoEstabelecimento, c.perfil);
  const simples = c.simplesNacional === "sim" ? "Sim" : c.simplesNacional === "nao" ? "Não" : "—";
  const docsAindaPendentes = (st.docsPend || []).filter((d) => !docsNosSistemas.has(d.tipo));
  const historico = Array.isArray(c.historico) ? [...c.historico].reverse() : [];

  const salvar = (patch, acao, detalhe, extra = {}) => {
    const rec = {
      ...c, ...patch,
      historico: [...(Array.isArray(c.historico) ? c.historico : []), {
        ts: new Date().toISOString(), acao, detalhe, autor: userName, ...extra,
      }],
    };
    setCredenciamentos((prev) => prev.map((x) => (x.id === c.id ? rec : x)));
  };

  const openFile = async (path) => {
    if (!path) return;
    const win = window.open("", "_blank");
    const url = await getSignedUrl(path);
    if (!url) { if (win) win.close(); return; }
    if (win) win.location.href = url; else window.location.href = url;
  };

  // Emite o Cartão CNPJ na Receita e anexa na ficha (não pede nada ao
  // fornecedor). Só para as fichas anteriores à emissão automática.
  const emitirCartao = async () => {
    setEmitindo(true); setEmitirMsg("");
    const r = await emitirCartaoCNPJ(c.id);
    setEmitindo(false);
    if (!r?.ok) { setEmitirMsg(r?.erro || "Não foi possível emitir agora."); return; }
    setCredenciamentos((prev) => prev.map((x) => (x.id !== c.id ? x : {
      ...x,
      documentos: r.documentos || x.documentos,
      documentoPath: x.documentoPath || r.path || null,
      inscricaoEstadual: x.inscricaoEstadual || r.inscricao_estadual || "",
    })));
  };

  const confirmarSemInteresse = () => {
    // Motivo obrigatório: é o que orienta a retomada daqui a três meses.
    if (!semIntMotivo.trim()) { setErroMotivo("Diga o motivo — é o que vai orientar a retomada."); return; }
    const quando = resumeDate || addMonths(getToday(), 3);
    salvar(
      { status: "sem_interesse", nextActionDate: quando, nextAction: "Retomar contato (sem interesse anterior)" },
      "sem_interesse",
      `Sem interesse no momento — ${semIntMotivo.trim()}. Retomar em ${quando}.`,
      { motivo: semIntMotivo.trim() },
    );
    setNoInterestOpen(false); setErroMotivo("");
  };

  const retomar = () => salvar(
    { status: "pendente", nextActionDate: null, nextAction: null },
    "retomada", "Contato retomado — de volta ao fluxo.",
  );

  return (
    <Modal open={!!ficha} onClose={onClose} title={c.nomeFantasia || c.razaoSocial || "Ficha de credenciamento"} w={580}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <Badge color={st.color}>{st.label}</Badge>
        {c.origem === "link" && <span style={{ fontSize: 9, fontWeight: 700, color: "#6d28d9", background: "#f5f3ff", padding: "2px 7px", borderRadius: 10 }}>VIA LINK</span>}
        {c.dispositivo && <span style={{ fontSize: 9, fontWeight: 700, color: "#0f766e", background: "#f0fdfa", padding: "2px 7px", borderRadius: 10 }}>{c.dispositivo === "mobile" ? "CELULAR" : "COMPUTADOR"}</span>}
        {(c.sistemas || []).map((id) => { const s = SISTEMAS.find((x) => x.id === id); return s ? <Badge key={id} color={s.color} small>{s.label}</Badge> : null; })}
        {!editando && (
          <button onClick={abrirEdicao} style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
            <Pencil size={12} /> Editar nome/contato
          </button>
        )}
      </div>

      {/* Edit rápido inline — nome e contato, sem abrir a ficha completa. */}
      {editando && (
        <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>Editar nome e contato</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              ["nomeFantasia", "Nome / estabelecimento"],
              ["razaoSocial", "Razão social"],
              ["responsavelNome", "Nome do contato"],
              ["telefone", "Telefone"],
              ["celular", "Celular / WhatsApp"],
              ["email", "E-mail"],
            ].map(([campo, rotulo]) => (
              <label key={campo} style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>
                {rotulo}
                <input
                  value={form[campo] || ""}
                  onChange={(e) => setForm((p) => ({ ...p, [campo]: e.target.value }))}
                  style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, background: "#fff", color: "#334155", fontFamily: "inherit" }}
                />
              </label>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button onClick={() => setEditando(false)} style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
            <button onClick={salvarEdicao} style={{ padding: "7px 14px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Salvar</button>
          </div>
        </div>
      )}

      {st.pend?.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 12, padding: "7px 10px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a" }}>
          <AlertCircle size={13} color="#f59e0b" /><span style={{ fontSize: 11, color: "#b45309" }}>Falta: {st.pend.join(", ")}</span>
        </div>
      )}

      {/* Cartão CNPJ: emitido na Receita pelo CNPJ. Fichas criadas antes disso
          não têm — o botão resolve uma a uma, sem pedir nada ao fornecedor. */}
      {!(c.documentos || []).some((d) => d.tipo === "cnpj") && (c.cnpj || "").replace(/\D/g, "").length === 14 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "9px 12px", borderRadius: 8, background: "#f5f3ff", border: "1px solid #ddd6fe", flexWrap: "wrap" }}>
          <FileText size={13} color="#6d28d9" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: "#6d28d9", flex: 1, minWidth: 180, lineHeight: 1.4 }}>
            Esta ficha ainda não tem o <strong>Cartão CNPJ</strong>. Dá para emitir na Receita agora — o fornecedor não precisa mandar.
          </span>
          <button onClick={emitirCartao} disabled={emitindo} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 600, cursor: emitindo ? "wait" : "pointer", opacity: emitindo ? 0.7 : 1 }}>
            {emitindo ? "Emitindo..." : "Emitir Cartão CNPJ"}
          </button>
        </div>
      )}
      {emitirMsg && <div style={{ fontSize: 11, color: "#b45309", marginTop: -6, marginBottom: 12 }}>{emitirMsg}</div>}

      {/* 2ª etapa: os documentos da empresa, com o botão que os pede ao lado
          da pendência — procurar o link em outra tela era o que fazia
          ninguém cobrar. */}
      {docsAindaPendentes.length > 0 && c.editToken && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12, padding: "9px 12px", borderRadius: 8, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
          <Paperclip size={13} color="#1d4ed8" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#1d4ed8", lineHeight: 1.4 }}>
              <strong>Documentos da empresa pendentes:</strong> {docsAindaPendentes.map((d) => d.label).join(", ")}. Não travam o cadastro.
            </div>
            <button
              onClick={() => {
                const tk = encodeFichaToken({ c: c.id, k: c.editToken, d: 1, s: c.sistemas, n: c.nomeFantasia || c.razaoSocial });
                window.open(documentosWhatsappUrl(c.celular || c.telefone, tk, c.nomeFantasia || c.razaoSocial, docsAindaPendentes.map((d) => d.label)), "_blank");
              }}
              style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 8, padding: "6px 12px", borderRadius: 7, border: "none", background: "#25D366", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
            >
              <MessageCircle size={12} /> Pedir documentos no WhatsApp
            </button>
          </div>
        </div>
      )}

      {/* Chegou por fora: o fornecedor enviou pelo link do próprio sistema.
          Não está na lista do CRM, mas está com quem precisava. */}
      {st.docsPend?.length > 0 && docsAindaPendentes.length < st.docsPend.length && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12, padding: "9px 12px", borderRadius: 8, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
          <CheckCircle2 size={13} color="#059669" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 11, color: "#166534", lineHeight: 1.4 }}>
            <strong>Já entregue direto no sistema:</strong> {st.docsPend.filter((d) => docsNosSistemas.has(d.tipo)).map((d) => d.label).join(", ")}. Não precisa pedir.
          </div>
        </div>
      )}

      <Secao title="Empresa">
        <Linha k="Razão Social" v={c.razaoSocial} /><Linha k="Nome Fantasia" v={c.nomeFantasia} />
        <Linha k="CNPJ" v={c.cnpj} /><Linha k="Inscrição Estadual" v={c.inscricaoEstadual} />
        <Linha k="Ramo de Atividade" v={c.atividade} /><Linha k="Tipo" v={TIPOS_ESTABELECIMENTO.find((t) => t.id === c.tipoEstabelecimento)?.label} />
        <Linha k="Simples Nacional" v={simples} />
      </Secao>

      <Secao title="Localização e contatos">
        <Linha k="Endereço" v={[c.rua, c.numero].filter(Boolean).join(", ") || c.enderecoCompleto} /><Linha k="Bairro" v={c.bairro} />
        <Linha k="CEP" v={c.cep} /><Linha k="Cidade / UF" v={[c.cidade, c.estado].filter(Boolean).join(" / ")} />
        <Linha k="Telefone" v={c.telefone} /><Linha k="Celular" v={c.celular} /><Linha k="E-mail" v={c.email} />
      </Secao>

      <Secao title="Quem assinou a ficha">
        <Linha k="Nome" v={c.responsavelNome} /><Linha k="CPF" v={c.responsavelDoc} />
        <Linha k="RG" v={c.responsavelRg} /><Linha k="Telefone" v={c.responsavelTelefone} />
        <Linha k="Cargo" v={c.responsavelCargo} /><Linha k="E-mail" v={c.responsavelEmail} />
      </Secao>

      {(c.representanteNome || c.representanteCargo) && (
        <Secao title="Representante legal da empresa">
          <Linha k="Nome" v={c.representanteNome} /><Linha k="Função" v={c.representanteCargo} />
        </Secao>
      )}

      <Secao title="Dados bancários">
        <Linha k="Banco" v={c.banco} /><Linha k="Agência" v={c.agencia} />
        <Linha k="Conta" v={c.contaCorrente} />
        <Linha k="Tipo de conta" v={c.tipoConta === "poupanca" ? "Poupança" : c.tipoConta === "corrente" ? "Corrente" : ""} />
        <Linha k="Titular" v={c.titularConta} /><Linha k="Chave PIX" v={c.chavePix} />
      </Secao>

      {ramo.length > 0 && (
        <Secao title={TIPOS_ESTABELECIMENTO.find((t) => t.id === c.tipoEstabelecimento)?.label || "Perfil do ramo"}>
          {ramo.map((l) => <Linha key={l.k} k={l.k} v={l.v} />)}
        </Secao>
      )}

      {(serv.length > 0 || esp.length > 0 || veic.length > 0) && (
        <Secao title="Serviços prestados">
          {veic.length > 0 && <Linha k="Veículos" v={veic.join(", ")} />}
          {serv.length > 0 && <Linha k="Serviços" v={serv.join(", ")} />}
          {esp.length > 0 && <Linha k="Especialidades" v={esp.join(", ")} />}
        </Secao>
      )}

      {(c.fazSocorro || secoesDaFicha(c.sistemas, c.tipoEstabelecimento).socorro) && (
      <Secao title="Socorro 24h / assistência em rodovias">
        <Linha k="Presta socorro" v={c.fazSocorro ? "Sim" : "Não"} />
        {c.fazSocorro && <>
          <Linha k="Tipos" v={assist.join(", ")} />
          <Linha k="Atendimento" v={resumoAtendimento(c)} />
          <Linha k="Rodovias" v={c.assistenciaRodovias ? (c.rodoviasAtendidas || "Sim") : "Não atende"} />
          <Linha k="Plantão" v={c.telefoneSocorro} />
        </>}
      </Secao>
      )}

      {/* Qual link mandar quem decide é o CNPJ, não quem está com o celular na
          mão. Um bloco por sistema em que a ficha entra. */}
      {(c.sistemas?.length ? c.sistemas : ["manutencao"]).map((sis) => (
        <VinculoManutencao key={sis} credenciamento={c} sistema={sis} onEstado={registrarEstadoSistema} />
      ))}

      {c.notes && <div style={{ padding: "10px 12px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", fontSize: 12, color: "#475569", marginBottom: 12 }}>{c.notes}</div>}

      {/* Ações */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
        {onEdit && (
          <button onClick={() => { onClose(); onEdit(c); }} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Pencil size={13} /> {st.id === "concluido" ? "Editar" : "Continuar"}
          </button>
        )}
        <button onClick={() => openFile(c.fichaPath)} disabled={!c.fichaPath} style={actBtn(!c.fichaPath)}><FileText size={12} /> Ver ficha</button>
        {(c.documentos || []).length > 0 && <button onClick={() => openFile(c.documentos[0].path)} style={actBtn(false)}><ImageIcon size={12} /> Documento</button>}
        {wa && <button onClick={() => window.open(wa, "_blank")} style={{ ...actBtn(false), color: "#059669", borderColor: "#a7f3d0" }}><MessageCircle size={12} /> WhatsApp</button>}
        {st.id !== "concluido" && c.editToken && (
          <button
            onClick={() => { const tk = encodeFichaToken({ c: c.id, k: c.editToken, s: c.sistemas, n: c.nomeFantasia || c.razaoSocial }); window.open(correcaoWhatsappUrl(c.celular || c.telefone, tk, c.nomeFantasia || c.razaoSocial), "_blank"); }}
            style={{ ...actBtn(false), color: "#b45309", borderColor: "#fcd34d" }}
            title="Enviar link de correção (reabre a mesma ficha preenchida)"
          >
            <Send size={12} /> Enviar p/ correção
          </button>
        )}
        {st.id === "sem_interesse" ? (
          <button onClick={retomar} style={{ ...actBtn(false), color: "#7c3aed", borderColor: "#ddd6fe" }}><RotateCcw size={12} /> Retomar contato</button>
        ) : st.id !== "concluido" ? (
          <button onClick={() => { setResumeDate(addMonths(getToday(), 3)); setSemIntMotivo(""); setErroMotivo(""); setNoInterestOpen(true); }} style={{ ...actBtn(false), color: "#d97706", borderColor: "#fed7aa" }}><Ban size={12} /> Sem interesse</button>
        ) : null}
        <button onClick={() => setVerHistorico((v) => !v)} style={actBtn(false)}><History size={12} /> Histórico ({historico.length})</button>
        {onExcluir && (
          <button onClick={() => { onClose(); onExcluir(c); }} style={{ ...actBtn(false), marginLeft: "auto", color: "#ef4444", borderColor: "#fecaca" }}><Trash2 size={12} /> Excluir</button>
        )}
      </div>

      {/* Sem interesse — motivo obrigatório, igual ao card da rede */}
      {noInterestOpen && (
        <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>Por que não seguiu?</div>
          <input
            value={semIntMotivo}
            onChange={(e) => { setSemIntMotivo(e.target.value); setErroMotivo(""); }}
            placeholder="Ex: já atende por outra rede, achou a taxa alta, vai avaliar em 2026"
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${erroMotivo ? "#ef4444" : "#fcd34d"}`, fontSize: 12, boxSizing: "border-box", marginBottom: erroMotivo ? 2 : 8, background: "#fff", color: "#334155" }}
          />
          {erroMotivo && <div style={{ fontSize: 10.5, color: "#dc2626", marginBottom: 8 }}>{erroMotivo}</div>}
          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>Quando retomar o contato?</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <input type="date" value={resumeDate} onChange={(e) => setResumeDate(e.target.value)} style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #fcd34d", fontSize: 12, background: "#fff", color: "#334155" }} />
            {[1, 2, 3, 6].map((m) => (
              <button key={m} onClick={() => setResumeDate(addMonths(getToday(), m))} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {m} {m === 1 ? "mês" : "meses"}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={confirmarSemInteresse} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#d97706", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Confirmar sem interesse</button>
            <button onClick={() => setNoInterestOpen(false)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
          </div>
        </div>
      )}

      {st.id === "sem_interesse" && c.nextActionDate && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 11.5, color: "#92400e", lineHeight: 1.4 }}>
          <CalendarClock size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Sem interesse — <strong>retomar em {c.nextActionDate}</strong>.{motivoDaRecusa(null, c) ? ` Motivo: ${motivoDaRecusa(null, c)}.` : ""} A ficha sai da fila de pendentes e fica no balde “Sem interesse”.</span>
        </div>
      )}

      {verHistorico && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
          {historico.length === 0 && <div style={{ fontSize: 11.5, color: "#94a3b8" }}>Sem registros no histórico desta ficha.</div>}
          {historico.map((h, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderTop: i ? "1px solid #f8fafc" : "none" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACAO_COLOR[h.acao] || "#94a3b8", marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: "#334155" }}>{ACAO_LABEL[h.acao] || h.acao}</div>
                {h.detalhe && <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4 }}>{h.detalhe}</div>}
              </div>
              <div style={{ fontSize: 10, color: "#94a3b8", whiteSpace: "nowrap", textAlign: "right" }}>
                {fmtDataHora(h.ts)}{h.autor ? <><br />{h.autor}</> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function Secao({ title, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0 16px" }}>{children}</div>
    </div>
  );
}

function Linha({ k, v }) {
  return (
    <div style={{ fontSize: 12, color: "#334155", padding: "3px 0", display: "flex", gap: 6 }}>
      <span style={{ color: "#94a3b8", minWidth: 96, flexShrink: 0 }}>{k}:</span>
      <span style={{ fontWeight: 500, wordBreak: "break-word" }}>{v || "—"}</span>
    </div>
  );
}

const actBtn = (disabled) => ({
  display: "flex", alignItems: "center", gap: 4, padding: "6px 10px",
  borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff",
  color: disabled ? "#cbd5e1" : "#475569", fontSize: 11, fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
});
